import React, { useState } from 'react';
import { PieChart, BarChartHorizontal, MousePointer, Users, Image, Video, Mail, DollarSign, Percent, Target } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения распределения бюджета и метрик по платформам
 */
const BudgetDistribution = ({ results, platforms }) => {
  const [activeTab, setActiveTab] = useState('budget');

  if (!results) return null;

  // Получение всех активных платформ
  const activePlatformIds = Object.keys(platforms).filter(id => platforms[id].enabled);
  
  // Получение названий платформ
  const getPlatformName = (platformId) => {
    const platformMap = {
      'search': 'Поисковая реклама',
      'social': 'Социальные сети',
      'display': 'Медийная реклама',
      'video': 'Видеореклама',
      'email': 'Email-маркетинг'
    };
    return platformMap[platformId] || platformId;
  };

  // Получение иконки для платформы
  const getPlatformIcon = (platformId) => {
    switch (platformId) {
      case 'search': return <MousePointer className="w-5 h-5" />;
      case 'social': return <Users className="w-5 h-5" />;
      case 'display': return <Image className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'email': return <Mail className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  // Определение цвета для платформы
  const getPlatformColor = (platformId) => {
    const colorMap = {
      'search': 'blue',
      'social': 'purple',
      'display': 'green',
      'video': 'red',
      'email': 'amber'
    };
    return colorMap[platformId] || 'gray';
  };

  // Функция для форматирования данных в зависимости от активной вкладки
  const getMetricValue = (platformId, tab) => {
    const platform = results.platforms[platformId];
    if (!platform) return '-';

    switch (tab) {
      case 'budget':
        return formatCurrency(platform.budget, 0);
      case 'cpc':
        return formatCurrency(platform.cpc, 1);
      case 'cpm':
        return formatCurrency(platform.cpm, 1);
      case 'ctr':
        return `${formatNumber(platform.ctr * 100, 2)}%`;
      case 'conversion':
        return formatNumber(platform.conversions, 0);
      case 'cpa':
        return platform.conversions > 0 ? formatCurrency(platform.cpa, 0) : '-';
      case 'roi':
        return `${formatNumber(platform.roi, 1)}%`;
      default:
        return '-';
    }
  };

  // Функция для получения заголовка метрики
  const getMetricTitle = (tab) => {
    switch (tab) {
      case 'budget': return 'Распределение бюджета';
      case 'cpc': return 'Стоимость клика (CPC)';
      case 'cpm': return 'Стоимость тысячи показов (CPM)';
      case 'ctr': return 'Показатель кликабельности (CTR)';
      case 'conversion': return 'Количество конверсий';
      case 'cpa': return 'Стоимость конверсии (CPA)';
      case 'roi': return 'Возврат инвестиций (ROI)';
      default: return 'Показатели';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 mb-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
          Распределение по платформам
        </h3>
      </div>

      {/* Вкладки метрик */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
          <button
            onClick={() => setActiveTab('budget')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'budget'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Бюджет
          </button>
          <button
            onClick={() => setActiveTab('cpc')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'cpc'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            CPC
          </button>
          <button
            onClick={() => setActiveTab('cpm')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'cpm'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            CPM
          </button>
          <button
            onClick={() => setActiveTab('ctr')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'ctr'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            CTR
          </button>
          <button
            onClick={() => setActiveTab('conversion')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'conversion'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Конверсии
          </button>
          <button
            onClick={() => setActiveTab('cpa')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'cpa'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            CPA
          </button>
          <button
            onClick={() => setActiveTab('roi')}
            className={`py-2 px-3 text-sm font-medium rounded-t-lg ${
              activeTab === 'roi'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            ROI
          </button>
        </div>
      </div>

      <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-4">
        {getMetricTitle(activeTab)}
      </h4>

      {/* Визуализация данных */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* График данных */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          {activePlatformIds.map((platformId, index) => {
            const platform = results.platforms[platformId];
            if (!platform) return null;
            
            // Определяем значение для отображения в зависимости от вкладки
            let value, maxValue;
            switch (activeTab) {
              case 'budget':
                value = platform.budget;
                maxValue = results.campaign.totalBudget;
                break;
              case 'cpc':
                value = platform.cpc;
                maxValue = Math.max(...Object.values(results.platforms).map(p => p.cpc || 0)) * 1.1;
                break;
              case 'cpm':
                value = platform.cpm;
                maxValue = Math.max(...Object.values(results.platforms).map(p => p.cpm || 0)) * 1.1;
                break;
              case 'ctr':
                value = platform.ctr * 100;
                maxValue = Math.max(...Object.values(results.platforms).map(p => (p.ctr || 0) * 100)) * 1.1;
                break;
              case 'conversion':
                value = platform.conversions;
                maxValue = Math.max(...Object.values(results.platforms).map(p => p.conversions || 0)) * 1.1;
                break;
              case 'cpa':
                value = platform.conversions > 0 ? platform.cpa : 0;
                maxValue = Math.max(...Object.values(results.platforms)
                  .filter(p => p.conversions > 0)
                  .map(p => p.cpa || 0)) * 1.1;
                break;
              case 'roi':
                value = platform.roi;
                maxValue = Math.max(
                  Math.max(...Object.values(results.platforms).map(p => p.roi || 0)) * 1.1,
                  10 // Минимальное значение для шкалы
                );
                break;
              default:
                value = 0;
                maxValue = 100;
            }
            
            const widthPercent = `${Math.round((value / maxValue) * 100)}%`;
            const color = getPlatformColor(platformId);
            
            return (
              <div key={platformId} className="mb-4 last:mb-0">
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center">
                    <div className={`text-${color}-500 mr-2`}>
                      {getPlatformIcon(platformId)}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getPlatformName(platformId)}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {getMetricValue(platformId, activeTab)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                  <div 
                    className={`bg-${color}-500 dark:bg-${color}-400 h-2.5 rounded-full`} 
                    style={{ width: widthPercent }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Таблица с данными */}
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Платформа
                </th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Показы
                </th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Клики
                </th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {activeTab === 'budget' ? 'Доля' : activeTab.toUpperCase()}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {activePlatformIds.map((platformId) => {
                const platform = results.platforms[platformId];
                if (!platform) return null;
                
                return (
                  <tr key={platformId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <div className={`text-${getPlatformColor(platformId)}-500 mr-2`}>
                          {getPlatformIcon(platformId)}
                        </div>
                        {getPlatformName(platformId)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-center">
                      {formatNumber(platform.impressions, 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-center">
                      {formatNumber(platform.clicks, 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right font-medium">
                      {activeTab === 'budget' 
                        ? `${formatNumber(platform.budgetPercent, 1)}%` 
                        : getMetricValue(platformId, activeTab)
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Подсказка по метрикам */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="mt-0.5">
            <Target className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Объяснение метрик</h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              <strong>CPC</strong> - средняя стоимость клика, <strong>CPM</strong> - стоимость тысячи показов, 
              <strong> CTR</strong> - показатель кликабельности (отношение кликов к показам), 
              <strong> CPA</strong> - стоимость одной конверсии, <strong>ROI</strong> - возврат инвестиций (насколько окупаются вложения).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetDistribution;